import {
  Component,
  Inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EditableImage } from './edit-image.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SelectOption } from '@bcl/design-system';

type DrawingEvent = { clientX: number; clientY: number };

@Component({
  selector: 'pc-edit-image',
  templateUrl: './edit-image.component.html',
  styleUrls: ['./edit-image.component.scss'],
})
export class EditImageComponent implements AfterViewInit {
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  public dataUrl: string | undefined;
  public isDirty = false;
  public readonly drawingColorOptions: SelectOption[] = [
    { value: '#26E600', label: '1Green' },
    { value: 'blue', label: '2Blue' },
    { value: 'red', label: '3Red' },
  ];
  public readonly toolbarForm: FormGroup;
  private readonly originalFile: File;

  private ctx!: CanvasRenderingContext2D;
  private imageObj = new Image();
  private isDrawing = false;
  private startX = 0;
  private startY = 0;
  private previewActive = false;
  private previewX = 0;
  private previewY = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) editableImage: EditableImage,
    private matDialogRef: MatDialogRef<EditImageComponent, EditableImage>,
    fb: FormBuilder
  ) {
    this.originalFile = editableImage.file;

    const reader = new FileReader();
    reader.onload = () => {
      this.dataUrl = reader.result as string;
      this.imageObj.src = this.dataUrl!;
      this.imageObj.onload = () => this.renderImage();
      this.isDirty = true;
    };
    reader.readAsDataURL(editableImage.file);

    this.toolbarForm = fb.group({
      drawingColor: [this.drawingColorOptions[0].value],
    });
  }

  public ngAfterViewInit(): void {
    this.ctx = this.canvasRef.nativeElement.getContext('2d')!;
    this.animatePreview();
  }

  public rotateLeft(): void {
    this.rotate(-90);
  }

  public rotateRight(): void {
    this.rotate(90);
  }

  public save(): void {
    if (!this.isDirty) {
      throw new Error('No changes to save');
    }

    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      canvas.getContext('2d')?.drawImage(image, 0, 0);
      canvas.toBlob(
        (blob) => {
          const file = new File([blob!], this.originalFile.name, {
            type: this.originalFile.type,
          });
          this.matDialogRef.close({ file });
        },
        this.originalFile.type,
        0.9
      );
    };
    image.src = this.dataUrl!;
  }

  public startDraw(event: DrawingEvent): void {
    this.isDrawing = true;
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    this.startX = this.previewX = (event.clientX - rect.left) * scaleX;
    this.startY = this.previewY = (event.clientY - rect.top) * scaleY;
    this.previewActive = true;
  }

  public draw(event: DrawingEvent): void {
    if (!this.isDrawing) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    this.previewX = (event.clientX - rect.left) * scaleX;
    this.previewY = (event.clientY - rect.top) * scaleY;
  }

  public endDraw(event: DrawingEvent): void {
    if (!this.isDrawing) {
      return;
    }
    this.isDrawing = false;
    this.previewActive = false;

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const endX = (event.clientX - rect.left) * scaleX;
    const endY = (event.clientY - rect.top) * scaleY;

    const width = endX - this.startX;
    const height = endY - this.startY;

    this.edit((context, _, image) => {
      context.drawImage(image, 0, 0);
      context.strokeStyle = this.toolbarForm.value.drawingColor;
      context.lineWidth = 3;
      context.setLineDash([]);
      context.strokeRect(this.startX, this.startY, width, height);
    });
  }

  private animatePreview(): void {
    requestAnimationFrame(() => this.animatePreview());

    if (!this.previewActive || !this.isDrawing) {
      return;
    }

    const width = this.previewX - this.startX;
    const height = this.previewY - this.startY;

    this.ctx.clearRect(
      0,
      0,
      this.canvasRef.nativeElement.width,
      this.canvasRef.nativeElement.height
    );
    this.ctx.drawImage(this.imageObj, 0, 0);
    this.ctx.strokeStyle = this.toolbarForm.value.drawingColor;
    this.ctx.lineWidth = 3;
    this.ctx.setLineDash([4]);
    this.ctx.strokeRect(this.startX, this.startY, width, height);
  }

  private renderImage(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = this.imageObj.width;
    canvas.height = this.imageObj.height;

    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.ctx.drawImage(this.imageObj, 0, 0);
  }

  private rotate(degrees: number): void {
    this.edit((context, canvas, image) => {
      const radians = (degrees * Math.PI) / 180;
      const rotated = Math.abs(degrees) % 180 === 90;

      const newWidth = rotated ? image.height : image.width;
      const newHeight = rotated ? image.width : image.height;

      canvas.width = newWidth;
      canvas.height = newHeight;

      context.translate(newWidth / 2, newHeight / 2);
      context.rotate(radians);
      context.drawImage(image, -image.width / 2, -image.height / 2);
    });
  }

  private edit(
    work: (
      context: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      image: HTMLImageElement
    ) => void
  ): void {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = image.width;
      canvas.height = image.height;

      work(context, canvas, image);

      this.dataUrl = canvas.toDataURL();
      this.isDirty = true;

      this.imageObj.src = this.dataUrl!;
      this.imageObj.onload = () => this.renderImage();
    };
    image.src = this.dataUrl!;
  }
}
